/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import Login from 'pages/Login'
import Home from 'pages/Home'
import ManualAnalysisAudit from 'pages/ManualAnalysisAudit'
import Statistics from 'pages/Statistics'

import { EmailSent, PasswordCreation, PasswordRecover, PasswordSuccess } from 'pages/PasswordManagement'

import {
  Forbidden,
  Liveness,
  LivenessCamera,
  LivenessFail,
  LivenessValidate,
  NotFound,
  Ocr,
  OcrFail,
  OcrOptions,
  OcrPreview,
  OcrUpload,
  Quiz,
  QuizFail,
  QuizFinished,
  QuizStart,
  Result,
  Start,
  VerifySignature,
} from 'pages/Antifraude'
import VerifyEmailSignature from 'pages/PasswordManagement/VerifyEmailSignature'
import DueDiligence from 'pages/DueDiligence'
import DueDiligenceResume from 'pages/DueDiligenceResume'
import DueDiligenceList from 'pages/DueDiligenceList'
import ProspectDashboard from 'pages/ProspectDashboard'
import ConfigOptions from 'pages/ConfigOptions'
import Dashboard from 'pages/Antifraude/Dashboard'
import Blocked from 'pages/Antifraude/Blocked'
import BlockHistory from 'pages/Antifraude/BlockHistory'
import ApplicationReview from 'pages/ApplicationReview'
import ApplicationDetails from 'pages/ApplicationDetails'
import BackgroundCheck from "pages/BackgroundCheck"

import { ROUTE_CONSTANTS } from 'utils/constants/routeConstants'

import { DefaultLayout, EmptyLayout, SignedInLayout } from 'layouts'

import SecuredRoute from './securedRoute'
import ProtectedRoute from './ProtectedRoute'
import OpenRouteWithLayout from './OpenRouteWithLayout'
import UserManagement from "../pages/UserManagement"
import UserDetails from "../pages/UserDetails"

const withLoginRoutes = () => (
  <Switch>
    <Route path={ROUTE_CONSTANTS.LOGIN} exact component={Login} />

    <Route path={ROUTE_CONSTANTS.ANTIFRAUDE} exact component={VerifySignature} />

    <Route
      path={ROUTE_CONSTANTS.ACCOUNT_CREATION}
      exact
      component={VerifyEmailSignature}
    />

    <Route
      path={ROUTE_CONSTANTS.PASSWORD_RECOVER}
      exact
      component={VerifyEmailSignature}
    />

    <OpenRouteWithLayout
      parent={DefaultLayout}
      child={PasswordRecover}
      exact
      path={ROUTE_CONSTANTS.FORGOT_PASSWORD}
    />

    <ProtectedRoute
      parent={DefaultLayout}
      child={PasswordCreation}
      exact
      path={ROUTE_CONSTANTS.PASSWORD_CREATION}
    />

    <ProtectedRoute
      parent={DefaultLayout}
      child={PasswordCreation}
      exact
      path={ROUTE_CONSTANTS.PASSWORD_REDEFINE}
    />

    <OpenRouteWithLayout
      parent={DefaultLayout}
      child={EmailSent}
      exact
      path={ROUTE_CONSTANTS.EMAIL_SENT}
    />

    <OpenRouteWithLayout
      parent={DefaultLayout}
      child={PasswordSuccess}
      exact
      path={ROUTE_CONSTANTS.PASSWORD_SUCCESS}
    />

    <OpenRouteWithLayout
      parent={DefaultLayout}
      child={Start}
      exact
      path={ROUTE_CONSTANTS.START}
    />

    <OpenRouteWithLayout
      parent={DefaultLayout}
      child={Forbidden}
      exact
      path={ROUTE_CONSTANTS.FORBIDDEN}
    />

    <ProtectedRoute
      parent={DefaultLayout}
      child={NotFound}
      exact
      level="prospect"
      path={ROUTE_CONSTANTS.NOT_FOUND}
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.QUIZ}
      parent={DefaultLayout}
      child={Quiz}
      level="prospect"
    />
    <ProtectedRoute
      path={ROUTE_CONSTANTS.QUIZ_START}
      parent={DefaultLayout}
      child={QuizStart}
      usecamera={false}
      level="prospect"
    />
    <ProtectedRoute
      path={ROUTE_CONSTANTS.QUIZ_FAIL}
      parent={DefaultLayout}
      child={QuizFail}
      level="prospect"
    />
    <ProtectedRoute
      path={ROUTE_CONSTANTS.QUIZ_FINISHED}
      parent={DefaultLayout}
      child={QuizFinished}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.OCR}
      parent={DefaultLayout}
      child={Ocr}
      usecamera={false}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.OCR_UPLOAD}
      parent={DefaultLayout}
      child={OcrUpload}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.OCR_OPTIONS}
      parent={DefaultLayout}
      child={OcrOptions}
      usecamera={false}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.OCR_PREVIEW}
      parent={EmptyLayout}
      child={OcrPreview}
      level="prospect"
    />
    <ProtectedRoute
      path={ROUTE_CONSTANTS.OCR_FAIL}
      parent={DefaultLayout}
      child={OcrFail}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.LIVENESS}
      parent={DefaultLayout}
      child={Liveness}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.LIVENESS_VALIDATE}
      parent={DefaultLayout}
      child={LivenessValidate}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.LIVENESS_FAIL}
      parent={DefaultLayout}
      child={LivenessFail}
      level="prospect"
    />
    <ProtectedRoute
      path={ROUTE_CONSTANTS.LIVENESS_CAMERA}
      parent={EmptyLayout}
      child={LivenessCamera}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.RESULT}
      parent={DefaultLayout}
      child={Result}
      usecamera={false}
      level="prospect"
    />

    <ProtectedRoute
      path={ROUTE_CONSTANTS.VALID_FAIL}
      parent={DefaultLayout}
      child={Result}
      usecamera={false}
      level="prospect"
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.HOME}
      exact
      parent={SignedInLayout}
      child={Home}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.BACKGROUND_CHECK}
      exact
      parent={SignedInLayout}
      child={BackgroundCheck}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.APPLICATION_REVIEW}
      parent={SignedInLayout}
      child={ApplicationReview}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.APPLICATION_DETAILS}
      parent={SignedInLayout}
      child={ApplicationDetails}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.PROSPECT_DASHBOARD}
      exact
      parent={SignedInLayout}
      child={ProspectDashboard}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.DUE_DILIGENCE}
      exact
      parent={SignedInLayout}
      child={DueDiligence}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.DUE_DILIGENCE_RESUME}
      exact
      parent={SignedInLayout}
      child={DueDiligenceResume}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.DUE_DILIGENCE_LIST}
      exact
      parent={SignedInLayout}
      child={DueDiligenceList}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.MANUAL_ANALISYS_AUDIT}
      exact
      parent={SignedInLayout}
      child={ManualAnalysisAudit}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.STATISTICS}
      exact
      parent={SignedInLayout}
      child={Statistics}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.RESTRICTIONS_OPTIONS}
      exact
      parent={SignedInLayout}
      child={ConfigOptions}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.USER_MANAGEMENT}
      exact
      parent={SignedInLayout}
      child={UserManagement}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.USER_EDITOR}
      exact
      parent={SignedInLayout}
      child={UserDetails}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.ANTIFRAUDE_DASHBOARD}
      exact
      parent={SignedInLayout}
      child={Dashboard}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.ANTIFRAUDE_BLOCKED}
      exact
      parent={SignedInLayout}
      child={Blocked}
    />

    <SecuredRoute
      path={ROUTE_CONSTANTS.ANTIFRAUDE_HISTORY_UNLOCK}
      exact
      parent={SignedInLayout}
      child={BlockHistory}
    />

    <Route component={Login} />
  </Switch>
)

const Router = () => {
  return <BrowserRouter>{withLoginRoutes()}</BrowserRouter>
}

export default Router
